import { useRouter } from 'next/router'
import React, { useEffect, useReducer } from 'react'
import { useToastContext } from 'src/lib/contexts/ToastContext'
import { getContentForId } from '../../../api/SabioApi'
import useI18n from '../../../lib/hooks/useI18n'
import { performReplacements } from '../../../lib/HtmlReplacements'
import { TreeChild, Tree } from '../../../types'
import Modal from '../../Modal'
import MultiMenu from '../MultiMenu'
import Tile from '../Tile'
import styles from './index.module.css'
import Tiles from './Tiles'

type event = any

type TileClickAction = {
  type: 'tileClick'
  payload: { event: event; activeTree: TreeChild }
}
type OpenModalAction = {
  type: 'openModal'
  payload: { title: string; content: string; leafId: string }
}
type CloseModalAction = { type: 'closeModal' }
type CloseMenuAction = { type: 'closeMenu' }

type Action =
  | TileClickAction
  | OpenModalAction
  | CloseModalAction
  | CloseMenuAction

interface State {
  isMenuOpen: boolean
  modalContent: {
    content: string
    title: string
  }
  tileClickEvent: {}
  isModalOpen: boolean
  activeTree?: TreeChild
  leafId?: string
}

const initialState: State = {
  isMenuOpen: false,
  modalContent: {
    content: '',
    title: '',
  },
  tileClickEvent: {},
  isModalOpen: false,
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'tileClick':
      return {
        ...state,
        isMenuOpen: true,
        tileClickEvent: action.payload.event,
        activeTree: action.payload.activeTree,
      }
    case 'openModal':
      return {
        ...state,
        modalContent: {
          title: action.payload.title,
          content: action.payload.content,
        },
        isModalOpen: true,
        isMenuOpen: false,
        leafId: action.payload.leafId,
      }
    case 'closeModal':
      return {
        ...state,
        isModalOpen: false,
        leafId: undefined,
      }
    case 'closeMenu':
      return {
        ...state,
        isMenuOpen: false,
        leafId: undefined,
      }
    default:
      throw new Error()
  }
}

const findTileByKey = (key: string, children?: TreeChild[]) => {
  return children?.find((child) => child.title === key)
}

interface Props {
  tree?: Tree
}

const TileGroup = ({ tree }: Props) => {
  const { locale, i18n } = useI18n()
  const router = useRouter()

  const { showToast } = useToastContext()
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (router.query['leafId']) {
      const leafId = router.query['leafId']
      if (leafId && typeof leafId === 'string') {
        handleLeafClick({}, leafId)
      }
    }
  }, [])

  const handleShare = () => {
    navigator.clipboard.writeText(
      `${window.location.href}?leafId=${state.leafId}`,
    )
    showToast({ text: i18n.urlCopiedToClipboard, severity: 'success' })
  }

  const handleTileClick = (event: any, activeTree: TreeChild) => {
    if (activeTree.leaf) {
      return handleLeafClick(event, (tree as Tree).id)
    }
    dispatch({
      type: 'tileClick',
      payload: { event, activeTree },
    })
  }

  const handleLeafClick = async (_event: any, leafId: string) => {
    try {
      const { title, content, attachments } = await getContentForId({
        treeId: leafId,
        lang: locale,
      })
      dispatch({
        type: 'openModal',
        payload: {
          title,
          leafId,
          content: performReplacements(content[0], attachments),
        },
      })
    } catch (e) {
      dispatch({
        type: 'openModal',
        payload: {
          leafId: '',
          title: 'No Content Found',
          content: 'For this resource is no content stored.',
        },
      })
    }
  }

  return (
    <div className={styles['container']}>
      <Modal
        title={state.modalContent.title}
        content={state.modalContent.content}
        mobileFullScreen
        isOpen={state.isModalOpen}
        handleClose={() => dispatch({ type: 'closeModal' })}
        handleShare={handleShare}
      />
      <MultiMenu
        isMenuOpen={state.isMenuOpen}
        event={state.tileClickEvent}
        activeTree={state.activeTree}
        handleClose={() => dispatch({ type: 'closeMenu' })}
        handleLeafClick={handleLeafClick}
      />

      {Tiles.map((tile) => (
        <Tile
          key={tile.key[locale]}
          path={tile.path}
          text={tile.text}
          treeChild={findTileByKey(
            tile.key[locale],
            tree?.children?.[0]?.children,
          )}
          handleTileClick={handleTileClick}
        />
      ))}
    </div>
  )
}

export default TileGroup
